import React, { useState } from 'react';
import {
  ArrowRight,
  CheckCircle,
  Zap,
  TrendingUp,
  Settings,
  Shield,
  Target,
  BarChart2,
  Clock,
  Activity,
  Cpu,
  MousePointer2,
  Lock,
  LineChart,
  Layers,
  ZapOff,
  Crosshair,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  MousePointerClick,
  Timer,
  Handshake,
  Sigma,
  Repeat,
  Split,
  SlidersHorizontal,
  Library,
  PlusCircle,
  LogOut,
  RefreshCw,
  Power,
  XCircle,
  ArrowLeftRight,
  ArrowRightLeft,
  Move,
  Layout,
  Gauge,
  BarChart3,
  AreaChart,
  Settings2,
  ShieldAlert,
  ArrowUpRight,
  UserPlus,
  Network,
  Wallet,
  Coins,
  ShieldInfo,
  MonitorPlay,
  Hammer,
  CandlestickChart,
  Puzzle,
  PlugZap,
  FastForward,
  ToyBrick,
  Bell,
  Workflow,
  Radio,
  Globe,
  Link2,
  Brain,
  ChevronDown,
  ChevronUp,
  Code2,
  Server,
  ChevronRight,
  Bot,
  Play,
  Cross,
  Shuffle,
} from 'lucide-react';
import './QuikTradeTerminal.scss';
import './Faq.scss';
import Header from './Header';
import Footer from './Footer';
import { Link } from 'react-router-dom';

import terminalImg from '../../../../assets/img/landing/qt-terminal.png';

const checkItems = [
  { icon: <CheckCircle size={18} />, label: '1-Click multi-leg execution' },
  { icon: <CheckCircle size={18} />, label: 'Built for options scalpers' },
  { icon: <CheckCircle size={18} />, label: 'Advanced risk engine' },
  { icon: <CheckCircle size={18} />, label: 'Multi-broker ready' },
  { icon: <CheckCircle size={18} />, label: 'Real-time PnL and Greeks' },
];

const orderPresets = [
  { icon: <Zap size={24} />, title: '1 Click Options Execution', desc: 'Execute any option strategy instantly.' },
  { icon: <Shuffle size={24} />, title: '1 Click Synthetic Future', desc: 'Construct synthetic futures in one click.' },
  { icon: <Split size={24} />, title: 'Straddle & Strangle', desc: 'With automated hedges in place.' },
  { icon: <SlidersHorizontal size={24} />, title: 'Custom Rules', desc: 'Your rules, our 1-click execution.' },
  { icon: <Library size={24} />, title: 'Ready-made Strategies', desc: 'Based on real-time market views.' },
];

const positionControls = [
  'Add positions',
  'Exit positions',
  'Reverse positions',
  'Risk ON or OFF',
];

const strategyActions = [
  'Exit Calls / Puts / All',
  'Convert Straddle to Strangle',
  'Convert Strangle to Straddle',
  'Move strikes UP / DOWN',
];

const multiStrategyFeatures = [
  'Manage multiple strategies simultaneously',
  'Each strategy with its own risk rules',
  'Account-level risk oversight',
  'Clean strategy segregation',
];

const monitoringFeatures = [
  {
    title: 'Real-time PnL tracking',
    desc: 'Watch your profits and losses move tick-by-tick with zero latency.',
    icon: <Activity size={24} />,
  },
  {
    title: 'MTM graph visualization',
    desc: 'Visual history of your mark-to-market performance across the session.',
    icon: <AreaChart size={24} />,
  },
  {
    title: 'Position Greeks management',
    desc: 'Monitor Delta, Theta, Gamma, and Vega live for every strategy.',
    icon: <Sigma size={24} />,
  },
  {
    title: 'Combined premium monitoring',
    desc: 'Track aggregate premiums and multi-leg costs in one unified view.',
    icon: <Layers size={24} />,
  },
  {
    title: 'Live LTP tracking',
    desc: 'Stream prices directly from the exchange for split-second decisions.',
    icon: <Zap size={24} />,
  },
];

const stopLossSystems = [
  'Combined premium',
  'LTP movement',
  'MTM movement',
];

const trailingFeatures = [
  'Percent movement',
  'Points movement',
  'MTM movement',
];

const protectionFeatures = [
  'Profit locking',
  'Profit trailing',
  'Dynamic protection',
];

const audience = [
  'Options scalpers',
  'Short straddle and strangle traders',
  'Intraday options buyers',
  'Multi-strategy traders',
  'Professional derivatives desks',
  'Algo assisted discretionary traders',
];

const whySwitch = [
  'Compress complex trades into 1 click',
  'Eliminate manual leg errors',
  'Manage risk professionally',
  'Scale multiple strategies easily',
  'Monitor Greeks and MTM live',
  'Trade faster than traditional terminals',
];

const faqs = [
  {
    q: 'What is QuikTrade Terminal?',
    a: 'QuikTrade Terminal is an advanced trading interface designed for professional options traders who need speed, multi-strategy management, and robust risk control.',
  },
  {
    q: 'Does it support multi-leg strategies?',
    a: 'Yes, it is built specifically for multi-leg options execution, allowing you to enter and exit complex strategies like straddles, strangles, and butterflies in a single click.',
  },
  {
    q: 'Can I trade with multiple brokers?',
    a: 'Absolutely. QuikTrade Terminal is multi-broker ready, allowing you to connect and manage trades across different supported brokers from one screen.',
  },
  {
    q: 'Is there a risk management system?',
    a: 'Yes, it features a powerful multi-layer risk engine that supports position-level, strategy-level, and account-level controls, including advanced SL/TP and trailing logic.',
  },
  {
    q: 'Does it have chart trading?',
    a: 'Yes, you can trade directly from the integrated charts for faster decision-making and visual execution.',
  },
];

const QuikTradeTerminal = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="tm-page poppins">
      <Header />

      {/* Hero Section */}
      <section className="tm-hero">
        <div className="tm-container">
          <div className="tm-hero-content">
            <h1>
              An <span>Advanced Terminal</span> for <br />
              Pro-active Traders
            </h1>
            <p className="tm-hero-sub">
              Analyze, Execute, and Manage Orders Easily and Efficiently
            </p>
            
            <div className="tm-preview">
              <img src={terminalImg} alt="QuikTrade Terminal" className="tm-main-screenshot" />
            </div>

            <div className="tm-hero-features-grid">
              {checkItems.map((item) => (
                <div key={item.label} className="tm-hero-feature-item">
                  <span className="tm-feature-icon">{item.icon}</span>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>

            <div className="tm-hero-cta">
              <Link to="/pricing" className="tm-btn tm-btn-primary">
                Start Trading Smarter <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Core Capability: One-Click Execution */}
      <section className="tm-section bg-dark text-white">
        <div className="tm-container">
          <div className="tm-section-header-centered">
            <h2 className="text-white">Execute Complex Strategies in One Click</h2>
            <p className="tm-blue-subh">
              Speed matters in options trading. QuikTrade Terminal compresses complex workflows into single-click actions.
            </p>
          </div>

          <div className="tm-order-presets-grid">
            {orderPresets.map((preset, idx) => (
              <div key={idx} className="tm-preset-card">
                <div className="tm-preset-icon">{preset.icon}</div>
                <h3>{preset.title}</h3>
                <p>{preset.desc}</p>
              </div>
            ))}
          </div>

          <div className="tm-execution-banner">
            <div className="tm-banner-stat">
              <span className="tm-label">From Idea to Execution in Seconds</span>
            </div>
          </div>
        </div>
      </section>

      {/* Adjustments Section */}
      <section className="tm-section">
        <div className="tm-container">
          <div className="tm-section-header-centered">
            <h2>Instant Position and Strategy Adjustments</h2>
            <p>Markets move fast. Your adjustments should be faster.</p>
          </div>

          <div className="tm-grid-2-cols">
            <div className="tm-feature-box">
              <div className="tm-box-header">
                <div className="tm-box-icon">
                  <SlidersHorizontal size={22} />
                </div>
                <h3>Position Level Controls</h3>
              </div>
              <p>With one click:</p>
              <ul className="tm-feature-list-simple">
                {positionControls.map((item) => (
                  <li key={item}><ArrowRight size={14} /> {item}</li>
                ))}
              </ul>
              <div className="tm-box-footer">No manual leg management.</div>
            </div>

            <div className="tm-feature-box">
              <div className="tm-box-header">
                <div className="tm-box-icon">
                  <Workflow size={22} />
                </div>
                <h3>Strategy Level Smart Actions</h3>
              </div>
              <p>Advanced traders get powerful automation tools:</p>
              <ul className="tm-feature-list-simple">
                {strategyActions.map((item) => (
                  <li key={item}><ArrowRight size={14} /> {item}</li>
                ))}
              </ul>
              <div className="tm-box-footer">Adjust entire strategies without rebuilding trades.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Multi-Strategy Management */}
      <section className="tm-section bg-primary-gradient text-white">
        <div className="tm-container">
          <div className="tm-section-header-centered">
            <h2 className="text-white">Built for Multi-Strategy Traders</h2>
            <p className="tm-blue-subh">
              Run your trading like a professional desk. QuikTrade Terminal provides the isolation and control needed for complex portfolios.
            </p>
          </div>

          <div className="tm-section-grid align-center">
            <div className="tm-section-text">
              <ul className="tm-feature-list-white">
                {multiStrategyFeatures.map((item) => (
                  <li key={item}><ShieldCheck color='#fff' size={18} /> {item}</li>
                ))}
              </ul>
            </div>
            <div className="tm-section-visual">
              <div className="tm-desk-viz">
                <div className="tm-desk-node">Account Level Oversight</div>
                <div className="tm-desk-connector" />
                <div className="tm-desk-children">
                  <div className="tm-desk-child">Strategy A</div>
                  <div className="tm-desk-child">Strategy B</div>
                  <div className="tm-desk-child">Strategy C</div>
                </div>
                <p className="tm-description">Scale without chaos.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trading Modes */}
      <section className="tm-section">
        <div className="tm-container">
          <div className="tm-section-header-centered">
            <h2>Two Powerful Trading Modes</h2>
            <p>Switch based on your trading style.</p>
          </div>

          <div className="tm-grid-2-cols">
            <div className="tm-mode-card">
              <div className="tm-mode-icon"><Gauge size={30} /></div>
              <h3>Scalper Mode</h3>
              <p>Designed for ultra-fast intraday traders.</p>
              <ul className="tm-mode-points">
                <li>Rapid order flow</li>
                <li>Minimal friction</li>
                <li>Execution-first design</li>
              </ul>
            </div>
            <div className="tm-mode-card">
              <div className="tm-mode-icon"><Workflow size={30} /></div>
              <h3>Strategy Mode</h3>
              <p>Built for structured options traders.</p>
              <ul className="tm-mode-points">
                <li>Multi-leg management</li>
                <li>Risk automation</li>
                <li>Strategy lifecycle control</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Analytics */}
      <section className="tm-section bg-dark text-white">
        <div className="tm-container">
          <div className="tm-section-header-centered">
            <h2 className="text-white">Advanced Monitoring and Analytics</h2>
            <p className="tm-blue-subh">
              Stay ahead of the market with institutional-grade visibility. 
              Our analytics engine processes millions of data points to give you real-time edge.
            </p>
          </div>

          <div className="tm-analytics-content">
            <div className="tm-analytics-cards">
              {monitoringFeatures.map((feature, idx) => (
                <div key={idx} className="tm-analytics-card">
                  <div className="tm-card-icon-blob">
                    {feature.icon}
                  </div>
                  <div className="tm-card-body">
                    <h4>{feature.title}</h4>
                    <p>{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="tm-analytics-visual-wrapper simplified">
              <div className="tm-pro-terminal-dashboard">
                <div className="tm-dashboard-top-row">
                  <div className="tm-stat-mini">
                    <span className="label">PORTFOLIO P&L</span>
                    <span className="value positive">+₹65,296.75</span>
                  </div>
                  <div className="tm-stat-mini">
                    <span className="label">UNREALIZED</span>
                    <span className="value positive">+₹76,606.75</span>
                  </div>
                  <div className="tm-stat-mini">
                    <span className="label">REALIZED</span>
                    <span className="value negative">-₹11,310.00</span>
                  </div>
                </div>
                
                <div className="tm-main-visual">
                  <div className="tm-visual-header-sub">
                    <span>Performance Analytics</span>
                    <div className="tm-live-indicator">
                      <span className="dot"></span> LIVE
                    </div>
                  </div>
                  <div className="tm-chart-area-simple">
                    <div className="tm-chart-line-pro"></div>
                  </div>
                </div>

                <div className="tm-greeks-pro-grid">
                  <div className="tm-greek-p-stat">
                    <span className="name">Delta</span>
                    <span className="val">+124.50</span>
                  </div>
                  <div className="tm-greek-p-stat">
                    <span className="name">Theta</span>
                    <span className="val negative">-85.20</span>
                  </div>
                  <div className="tm-greek-p-stat">
                    <span className="name">Gamma</span>
                    <span className="val">+12.42</span>
                  </div>
                  <div className="tm-greek-p-stat">
                    <span className="name">Vega</span>
                    <span className="val">+42.15</span>
                  </div>
                </div>
              </div>
              
              <div className="tm-performance-footer">
                <span>Know your risk at every second.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Risk Management */}
      <section className="tm-section">
        <div className="tm-container">
          <div className="tm-section-header-centered">
            <h2>Powerful Risk Management Engine</h2>
            <p>QuikTrade Terminal protects your capital with multi-layer controls.</p>
          </div>

          <div className="tm-risk-grid">
            <div className="tm-risk-card">
              <div className="tm-risk-card-header">
                <div className="icon-box">
                  <Target size={22} />
                </div>
                <h4>Risk Levels Supported</h4>
              </div>
              <div className="tm-risk-pills">
                <span>Position Level</span>
                <span>Strategy Level</span>
                <span>Account Level</span>
              </div>
            </div>

            <div className="tm-risk-card">
              <div className="tm-risk-card-header">
                <div className="icon-box">
                  <Crosshair size={22} />
                </div>
                <h4>Smart Stop Loss & Target</h4>
              </div>
              <p className="text-sm mb-2">Configure based on:</p>
              <div className="tm-risk-check-list">
                {stopLossSystems.map(item => <span key={item}><CheckCircle size={14} /> {item}</span>)}
              </div>
            </div>

            <div className="tm-risk-card">
              <div className="tm-risk-card-header">
                <div className="icon-box">
                  <TrendingUp size={22} />
                </div>
                <h4>Advanced Trailing Stop Loss</h4>
              </div>
              <p className="text-sm mb-2">2-step intelligent trailing:</p>
              <div className="tm-risk-check-list">
                {trailingFeatures.map(item => <span key={item}><CheckCircle size={14} /> {item}</span>)}
              </div>
              <div className="tm-protection-strip">
                {protectionFeatures.map(item => <span key={item}>{item}</span>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chart Trading & Brokers */}
      <section className="tm-section bg-gray">
        <div className="tm-container">
          <div className="tm-grid-2-cols">
            <div className="tm-feature-block">
              <div className="tm-icon-wrap"><Crosshair size={24} /></div>
              <h3>Chart Trading Built In</h3>
              <p>Trade directly from charts without switching screens.</p>
              <ul className="tm-simple-list">
                <li>Faster decision making</li>
                <li>Visual execution</li>
                <li>Seamless workflow</li>
              </ul>
              <p className="tm-tag-line">Perfect for discretionary traders.</p>
            </div>

            <div className="tm-feature-block">
              <div className="tm-icon-wrap"><Handshake size={24} /></div>
              <h3>Unlimited Broker Integrations</h3>
              <p>Trade where you want without platform lock-in.</p>
              <ul className="tm-simple-list">
                <li>Multi-broker connectivity</li>
                <li>Strategy portability</li>
                <li>Scalable architecture</li>
              </ul>
              <p className="tm-tag-line">One terminal. Total freedom.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Audience */}
      <section className="tm-section bg-dark text-white">
        <div className="tm-container">
          <div className="tm-section-header-centered">
            <h2 className="text-white">Who Is This For?</h2>
            <p className="tm-blue-subh opacity-75">If speed and control matter to you, this is built for you</p>
          </div>

          <div className="tm-section-grid align-center">
            <div className="tm-section-text">
              <div className="tm-audience-cards-grid">
                <div className="tm-audience-card">
                  <div className="icon"><Timer size={20} /></div>
                  <div className="tm-content">
                    <h4>Scalpers & Intraday</h4>
                    <p>Execute multi-leg strategies in milliseconds during volatility.</p>
                  </div>
                </div>

                <div className="tm-audience-card">
                  <div className="icon"><Split size={20} /></div>
                  <div className="tm-content">
                    <h4>Hedged Playbook</h4>
                    <p>Manage Iron Condors, Straddles, and spreads with visibility.</p>
                  </div>
                </div>

                <div className="tm-audience-card">
                  <div className="icon"><Sigma size={20} /></div>
                  <div className="tm-content">
                    <h4>Alpha Seekers</h4>
                    <p>Traders who need real-time Greeks and risk controls.</p>
                  </div>
                </div>

                <div className="tm-audience-card">
                  <div className="icon"><Network size={20} /></div>
                  <div className="tm-content">
                    <h4>Systems Focused</h4>
                    <p>Bridge signals to terminal with unlimited broker flexibility.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="tm-section-box tm-advantage-hero-box">
              <div className="tm-box-header">
                <h3>The Strategic Advantage</h3>
              </div>
              <ul className="tm-advantage-list">
                {whySwitch.map(item => (
                  <li key={item}>
                    <div className="bullet"><ShieldCheck size={14} /></div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="tm-box-footer">
                Built for the 1% of traders who refuse to settle.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq">
        <div className="container">
          <div className="header">
            <h2>Frequently Asked Questions</h2>
            <p>Got questions about the Terminal? We've got answers</p>
          </div>
          <div className="faq-list">
            {faqs.map(({ q, a }, i) => (
              <div key={i} className="faq-item">
                <button
                  className="faq-button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <div className="button-content">
                    <span className="question">{q}</span>
                    <svg
                      className={`icon ${openFaq === i ? 'open' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth="2" 
                        d="M12 6v12M6 12h12"
                      />
                    </svg>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="faq-answer">
                    <p>{a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="tm-cta">
            <h2>Ready to Trade Like a Pro?</h2>
            <p>Upgrade from basic terminals to execution built for serious options traders.</p>
            
            <div className="tm-cta-features">
              <span><CheckCircle size={18} /> Faster execution</span>
              <span><CheckCircle size={18} /> Smarter risk control</span>
              <span><CheckCircle size={18} /> Cleaner strategy management</span>
            </div>

            <Link to="/pricing" className="tm-btn tm-btn-secondary">
              Start Using QuikTrade Terminal Today <ArrowRight size={18} />
            </Link>
      </section>

      <Footer />
    </div>
  );
};

export default QuikTradeTerminal;
